import React from "react";
import { formatDeliveryWindow } from "../../utils/checkoutTotals";

// What the PIN check says, in words. `pinCheck` is the answer of /settings/pincode/:pin:
//   { status: idle | checking | valid | invalid | unverified, city?, state?, serviceable?, deliveryFrom?, deliveryTo? }
export function PinStatus({ pinCheck }) {
  if (!pinCheck || pinCheck.status === "idle") return null;
  if (pinCheck.status === "checking") return <small className="pin-status">Checking PIN code...</small>;
  if (pinCheck.status === "invalid") {
    return <small className="pin-status pin-bad">This PIN code was not found. Please check it.</small>;
  }
  if (pinCheck.status === "valid" && pinCheck.serviceable === false) {
    return (
      <small className="pin-status pin-bad">
        Sorry, we cannot deliver to this PIN code yet. Please use a different delivery address.
      </small>
    );
  }
  if (pinCheck.status === "valid") {
    const by = formatDeliveryWindow(pinCheck.deliveryFrom, pinCheck.deliveryTo);
    return (
      <small className="pin-status pin-ok">
        ✓ Delivery available to your PIN code{pinCheck.city ? ` (${pinCheck.city}, ${pinCheck.state})` : ""}
        {by && <strong className="pin-delivery-by"> · {by}</strong>}
      </small>
    );
  }
  return null; // unverified: the lookup was unavailable, so the order is allowed to continue
}

// The address a parcel is sent to, one clear field per part:
// recipient, phone, house / flat no., street / area, landmark, PIN code (fills city and state), city, state.
export default function AddressFields({ value, onChange, pinCheck, idPrefix = "addr" }) {
  const field = (name, label, props = {}) => (
    <div className={`address-field ${props.full ? "full-width" : ""}`}>
      <label htmlFor={`${idPrefix}-${name}`}>{label}</label>
      <input
        id={`${idPrefix}-${name}`}
        name={name}
        value={value[name] || ""}
        onChange={(e) => onChange(name, e.target.value)}
        {...props.input}
      />
      {props.after}
    </div>
  );

  return (
    <div className="address-form-grid">
      {field("fullName", "FULL NAME", { input: { type: "text", autoComplete: "name", placeholder: "Recipient's full name", required: true } })}
      {field("phone", "PHONE", {
        input: { type: "tel", inputMode: "numeric", autoComplete: "tel-national", placeholder: "10 digit mobile number", maxLength: 10, required: true },
      })}
      {field("line1", "HOUSE / FLAT NO.", {
        full: true,
        input: { type: "text", autoComplete: "address-line1", placeholder: "House / flat number, building name", required: true },
      })}
      {field("line2", "STREET / AREA", {
        full: true,
        input: { type: "text", autoComplete: "address-line2", placeholder: "Street, locality, area", required: true },
      })}
      {field("landmark", "LANDMARK (OPTIONAL)", {
        full: true,
        input: { type: "text", placeholder: "Near a temple, school, shop ...", maxLength: 150 },
      })}
      {field("pincode", "PIN CODE", {
        input: { type: "text", inputMode: "numeric", autoComplete: "postal-code", placeholder: "6 digit PIN code", maxLength: 6, required: true },
        after: <PinStatus pinCheck={pinCheck} />,
      })}
      {field("city", "CITY", { input: { type: "text", autoComplete: "address-level2", placeholder: "City", required: true } })}
      {field("state", "STATE", { input: { type: "text", autoComplete: "address-level1", placeholder: "State", required: true } })}
    </div>
  );
}

// One line of an address, for lists: "Flat 4B, MG Road, Karol Bagh (Landmark: Near the metro)".
export const addressLine = (a) =>
  [a.line1, a.line2].filter(Boolean).join(", ") + (a.landmark ? ` (Landmark: ${a.landmark})` : "");
